import React from "react";
import StockCardView from "../cards/StockCardView";
import EventCardView from "../cards/EventCardView";
import IndexCardView from "../cards/IndexCardView";

const GameRules = ({ variant = "card", isExpanded, onToggle, showTitle = true }) => {
  // Example stock cards for visual reference
  const exampleStocks = [
    {
      name: "Neural Link Corp",
      baseCost: 16,
      growth: 4,
      dividend: 0,
      industrySector: "tech",
      description: "Neural interfaces connecting human minds directly to computers. The next evolution of interaction.",
      priceBreakdown: {
        marketPrice: 20,
        baseCost: 16,
        indexPrice: 2,
        growthCost: 2,
        stocksOwned: 0,
        manipulatorEffect: 0,
        previousPrice: 18,
        priceChange: 2
      }
    },
    {
      name: "Gold Hoard Bank",
      baseCost: 20,
      growth: 2,
      dividend: 6,
      industrySector: "finance",
      description: "Ancient institution hoarding wealth in marble vaults. Still works somehow.",
      priceBreakdown: {
        marketPrice: 22,
        baseCost: 20,
        indexPrice: 0,
        growthCost: 0,
        stocksOwned: 0,
        manipulatorEffect: 0,
        previousPrice: 22,
        priceChange: 0
      }
    },
    {
      name: "AI Everything Inc",
      baseCost: 13,
      growth: 3,
      dividend: 1,
      industrySector: "tech",
      description: "Adding 'AI' to everything and charging 10x more. Revolutionary.",
      priceBreakdown: {
        marketPrice: 19,
        baseCost: 13,
        indexPrice: 2,
        growthCost: 3,
        stocksOwned: 1,
        manipulatorEffect: 1,
        previousPrice: 17,
        priceChange: 2
      },
      appliedActionCards: []
    },
    {
      name: "Tim or Fee Bank",
      baseCost: 17,
      growth: 5,
      dividend: -3,
      industrySector: "finance",
      description: "A bank so big it charges its own investors fees. Peak capitalism.",
      priceBreakdown: {
        marketPrice: 25,
        baseCost: 17,
        indexPrice: 1,
        growthCost: 5,
        stocksOwned: 1,
        manipulatorEffect: 2,
        previousPrice: 22,
        priceChange: 3
      },
      appliedActionCards: [
        {
          name: "FOMO",
          actionType: "delayed_manipulate",
          effectValue: 5,
          processed: true,
          emoji: "🚀"
        }
      ]
    }
  ];

  // Example event cards for visual reference
  const exampleEvents = [
    {
      name: "AI Breakthrough",
      description: "Robots get smarter. Your job gets nervous. Shareholders throw a party.",
      timing: "start",
      effects: [
        { indexName: "tech", priceChange: 12 }
      ],
      status: "resolved"
    },
    {
      name: "Tech Bubble",
      description: "Tech stocks surge as investors discover 'this time is different.' Spoiler: it isn't.",
      timing: "start",
      effects: [
        { indexName: "tech", priceChange: 10 }
      ],
      conditionalEffects: {
        timing: "end",
        dieRoll: { min: 1, max: 6, success: [1, 2] },
        effects: [
          { indexName: "tech", priceChange: -15 }
        ]
      },
      status: "active",
      roundsActive: 2
    },
    {
      name: "Monetary Stimulus",
      description: "Central bank discovers the 'print money' button. Inflation? That's a problem for future economists (and everyone else).",
      timing: "start",
      effects: [
        { indexName: "tech", priceChange: 12 },
        { indexName: "finance", priceChange: 12 },
        { indexName: "industrial", priceChange: 12 },
        { indexName: "Health & Science", priceChange: 12 }
      ],
      status: "resolved"
    }
  ];

  // Example index cards
  const exampleIndexes = [
    { name: "tech", price: 20, emoji: "💻" },
    { name: "finance", price: 10, emoji: "🏦" },
    { name: "industrial", price: 11, emoji: "🏭" },
    { name: "Health & Science", price: 15, emoji: "🧬" }
  ];

  const cardSize = { width: "180px", minHeight: "240px" };

  const rulesContent = (
    <div className="rules-content">
      <div className="rule-section" style={{ 
        backgroundColor: 'rgba(220, 38, 38, 0.08)', 
        padding: '20px', 
        borderRadius: '8px',
        marginBottom: '20px',
        border: '2px solid rgba(220, 38, 38, 0.3)',
        boxShadow: '0 2px 8px rgba(220, 38, 38, 0.15)'
      }}>
        <h3 style={{ marginTop: 0, color: '#dc2626', display: 'flex', alignItems: 'center', gap: '8px' }}>
          ⚠️ Important Disclaimer
        </h3>
        <p style={{ fontSize: '1rem', lineHeight: '1.6', marginBottom: '12px', color: '#333' }}>
          <strong>This game does not endorse pump and dump investing.</strong> This game highlights the dangers of 
          investing based on hype and hearsay, and satirizes the absurdity of modern markets in a fun and 
          entertaining way.
        </p>
        <p style={{ fontSize: '0.95rem', lineHeight: '1.6', marginBottom: 0, fontStyle: 'italic', color: '#666' }}>
          <strong>Always seek professional financial advice and do your own research</strong> before making any 
          real-world investment decisions. This is a game for entertainment purposes only.
        </p>
      </div>

      <div className="rule-section" style={{ 
        backgroundColor: 'rgba(255, 255, 255, 0.03)', 
        padding: '20px', 
        borderRadius: '8px',
        marginBottom: '20px',
        border: '1px solid rgba(255, 255, 255, 0.1)'
      }}>
        <h3 style={{ marginTop: 0 }}>📖 The Story</h3>
        <p style={{ fontSize: '1.05rem', lineHeight: '1.7', marginBottom: '16px' }}>
          You are a <strong>finfluencer investor</strong> who has just received a <strong>60 Kapital</strong> windfall. 
          Whether it came from a viral TikTok video, an unexpected windfall, or an inheritance from your 
          wealthy uncle, the source doesn't matter—what matters is that you now have capital to play with.
        </p>
        <p style={{ fontSize: '1.05rem', lineHeight: '1.7', marginBottom: '16px' }}>
          A <strong>brand new stock market</strong> has just opened its doors, and you're among the first to get in. 
          This isn't your grandfather's stock market—this is the wild west of modern finance, where memes move markets, 
          influencers create bubbles, and regulatory oversight is... well, let's just say it's "flexible."
        </p>
        <p style={{ fontSize: '1.05rem', lineHeight: '1.7', marginBottom: '16px' }}>
          Your objective is simple: <strong>end the game with the highest net worth</strong>. But achieving this is anything 
          but simple. You'll need to master the art of trading assets, timing the market, and—let's be honest—
          <strong> sabotaging your opponents</strong> whenever possible. Use insider trading, spread hype, create scandals, 
          and manipulate the market to your advantage. After all, in this game, ethics are optional.
        </p>
        <p style={{ fontSize: '1.05rem', lineHeight: '1.7', marginBottom: 0, fontStyle: 'italic', color: '#888' }}>
          <strong>Pump N Dump</strong> simulates how modern markets actually behave, with a healthy dash of satire to highlight 
          the absurdity of contemporary finance and capitalism. Welcome to the game where everyone's a winner... until they're not.
        </p>
      </div>

      <div className="rule-section">
        <h3>🎯 Objective</h3>
        <p>Finish with the highest net worth (cash + stock sell value) to win! The game lasts for a maximum of 10 rounds, with the number of rounds calculated to ensure each player gets at least one turn in each starting position. The number of rounds is the largest multiple of the number of players that doesn't exceed 10.</p>
      </div>

      <div className="rule-section">
        <h3>🔄 Game Phases</h3>
        <p>Each round progresses through four phases with timed transitions:</p>
        <ol className="rules-list" style={{ paddingLeft: '20px' }}>
          <li><strong>📊 Stock Options:</strong> New stocks are drawn (Round 1) or existing stocks are updated. Players can see available stocks and plan their strategy. The number of stocks scales with the number of players to encourage competition and Pump N Dump strategies.</li>
          <li><strong>📰 Events:</strong> 
            <ul style={{ marginTop: '8px', paddingLeft: '20px' }}>
              <li>Existing events from the previous round are resolved (bubbles may pop, end-of-round effects trigger)</li>
              <li>Resolved events are cleaned up</li>
              <li>A new event is drawn and activated</li>
              <li>Bubble growth, delayed manipulators, and start-of-round events are processed</li>
            </ul>
          </li>
          <li><strong>💼 Trading:</strong> Players take turns buying, selling, and using action cards. Each player gets 40 seconds per action (3 actions = 120 seconds, 4 actions = 160 seconds). The turn order rotates each round (shuffled on Round 1, then rotated). Disconnected players' turns are automatically skipped.</li>
          <li><strong>🎲 End of Round:</strong> 
            <ul style={{ marginTop: '8px', paddingLeft: '20px' }}>
              <li><strong>Stock prices update</strong> based on ownership changes from buys and sells during the round - prices reflect the new ownership counts</li>
              <li>Delayed manipulator cards (FOMO, Sinking Ship) take effect at the end of the round, affecting prices after ownership updates</li>
              <li>Dividends are paid on even rounds (Round 2, 4, 6, 8) - players receive cash equal to the dividend value of each stock they own, plus index dividends (10% for Tech and Health & Science, 30% for Finance and Industrial)</li>
              <li>After these resolve, the round increments and returns to Stock Options phase</li>
            </ul>
          </li>
        </ol>
        <p style={{ marginTop: '12px', fontSize: '0.95rem', color: '#888' }}>
          <strong>Note:</strong> Players cannot take actions during phase transitions or the End of Round phase - these are for observing market changes.
        </p>
      </div>

      <div className="rule-section">
        <h3>👥 Turn Order</h3>
        <ul className="rules-list">
          <li>At the start of <strong>Round 1</strong>, the turn order is <strong>randomly shuffled</strong></li>
          <li>For subsequent rounds, the turn order <strong>rotates</strong> - the first player moves to the end, and the second player becomes first</li>
          <li>This ensures fairness - each player gets to be first player an equal number of times</li>
          <li>The game length is calculated so every player gets to be first player at least twice</li>
          <li>Turn order is displayed at the start of each round in the game log</li>
          <li>Players take turns in order until all players have completed their turn, then the round ends</li>
        </ul>
      </div>

      <div className="rule-section">
        <h3>⚡ Turns & Actions</h3>
        <ul className="rules-list">
          <li>Each player gets <strong>3 actions per turn</strong> (4 actions for 6-round games to compensate for shorter game length). When the optional rule <strong>First Player End-of-Round Action</strong> is on (default), the first player gets <strong>2 actions</strong> during their turn and <strong>1 action at end of round</strong> after everyone else has acted (same timer applies).</li>
          <li>Each turn has a timer based on actions: <strong>40 seconds per action</strong> (3 actions = 120 seconds, 4 actions = 160 seconds) - if time expires, the turn automatically advances. The end-of-round action for the first player uses the same timer (e.g. 40 seconds for 1 action).</li>
          <li>If a player disconnects, their turn is automatically skipped (they remain in the game and can reconnect)</li>
          <li>Actions can be used to:
            <ul style={{ marginTop: '8px', paddingLeft: '20px' }}>
              <li><strong>Buy stocks:</strong> Purchase a stock at its current price (costs 1 action). <strong>Maximum 2 stock purchases per turn</strong> (you can buy two shares total, either two of the same stock or two different stocks).</li>
              <li><strong>Sell stocks:</strong> Sell a stock you own at its current price (costs 1 action). <strong>You can sell multiple stocks per turn, including multiple shares of the same stock.</strong> However, you cannot buy and sell the same stock in the same turn.</li>
              <li><strong>Draw action cards:</strong> Draw a card from the action deck (costs 1 action, max 6 cards in hand)</li>
              <li><strong>Play action cards:</strong> Use an action card from your hand (costs 1 action)</li>
              <li><strong>Discard & Redraw:</strong> Discard 1-3 action cards from your hand and draw the same number of new cards (costs 1 action, once per turn)</li>
            </ul>
          </li>
          <li>When your turn starts, you'll see your actions remaining (⚡)</li>
          <li>Your turn automatically ends when you use all your actions</li>
          <li>You can manually end your turn early by clicking "End Turn"</li>
        </ul>
      </div>

      <div className="rule-section">
        <h3>⚙️ Optional Rules (Game Options in Lobby)</h3>
        <p>The <strong>host</strong> can toggle these options in the lobby before starting the game. Other players see them as read-only.</p>
        <ul className="rules-list">
          <li><strong>₿ Crypto Sector:</strong> Adds crypto stocks and the crypto index to the game (default: off)</li>
          <li><strong>📈 Index Buying:</strong> Allow players to purchase sector index cards (default: on). When off, indexes are not purchasable.</li>
          <li><strong>🎁 Dividend Action Card:</strong> On dividend rounds (except the final round), all players receive 1 free action card after dividends are paid (default: on). Players with a full hand (6 cards) do not receive the bonus.</li>
          <li><strong>🫧 Bubble Events:</strong> Include bubble event cards that grow over time and can pop with big index swings (default: off)</li>
          <li><strong>⏰ First Player End-of-Round Action:</strong> First player gets 2 actions during their turn, then 1 action at end of round after everyone else has acted. Same turn timer is used for the end-of-round action (default: on).</li>
        </ul>
      </div>

      <div className="rule-section">
        <h3>💰 Stocks</h3>
        <ul className="rules-list">
          <li><strong>Stock Board Configuration:</strong> The number of stocks scales with the number of players to create scarcity and encourage Pump N Dump strategies:
            <ul style={{ marginTop: '8px', paddingLeft: '20px' }}>
              <li><strong>Starting stocks:</strong> Number of players + 1 (e.g., 2 players = 3 stocks, 4 players = 5 stocks)</li>
              <li><strong>New stocks per round:</strong> Ceiling of (Number of players ÷ 3), minimum 1 (e.g., 2 players = 1 new, 3 players = 1 new, 4 players = 2 new). 6-round games get +1 additional new stock per round.</li>
              <li><strong>Maximum stocks on board:</strong> Players + half players (rounded up), minimum 6 (e.g., 2 players = 6 max, 4 players = 6 max, 6 players = 9 max). 6-round games get +1 additional max stock.</li>
              <li>Unpurchased stocks are removed at the end of each round, keeping the board dynamic</li>
            </ul>
          </li>
          <li><strong>Stock Price Formula:</strong> Stock price = <strong>💲 Base Cost + 📈 Index Price + 📈 (Growth × Shares Owned at Round Start) ± 🎴 Manipulator Effects</strong></li>
          <li><strong>Formula Breakdown:</strong>
            <ul style={{ marginTop: '8px', paddingLeft: '20px' }}>
              <li><strong>💲 Base Cost:</strong> The stock's base price (shown on the card)</li>
              <li><strong>📈 Index Price:</strong> The current price of the stock's sector index (Tech 💻, Finance 🏦, Industrial 🏭, or Health & Science 🧬). Each stock shows its index emoji in the top left of the card header.</li>
              <li><strong>📈 Growth × Shares:</strong> The stock's growth rate multiplied by the number of shares owned at the start of the round. This creates a pump effect - the more shares owned, the higher the price.</li>
              <li><strong>± 🎴 Manipulator Effects:</strong> Action cards that affect stock prices:
                <ul style={{ marginTop: '4px', paddingLeft: '20px' }}>
                  <li><strong>+5:</strong> Delayed manipulator cards (Influencer Hype, Merger Speculation, Analyst Hype) - applied at end of current round</li>
                  <li><strong>-5:</strong> Delayed manipulator cards (CEO Scandal, Accounting Scandal, Executive Misconduct) - applied at end of current round</li>
                </ul>
                Delayed manipulator effects take effect at the end of the round, ensuring they impact final prices and scoring.
              </li>
            </ul>
          </li>
          <li>Each stock belongs to an index sector: <strong>Tech 💻</strong>, <strong>Finance 🏦</strong>, <strong>Industrial 🏭</strong>, or <strong>Health & Science 🧬</strong></li>
          <li><strong>Price Calculation:</strong> Stock prices are calculated using the number of shares owned. During the round, prices remain constant based on ownership counts from the <strong>start of the round</strong>. At the <strong>end of the round</strong>, prices update to reflect ownership changes from buys and sells during the round. <strong>Delayed manipulator effects (🎴) take effect at the end of the round</strong>, affecting prices after ownership updates. This ensures the final round's trading activity impacts final prices before scoring.</li>
          <li><strong>Growth:</strong> The more shares owned at the start of the round, the higher the price. All players pay the same price during a round based on the ownership count from the round's beginning.</li>
          <li><strong>Price Growth Cap:</strong> To prevent excessive price inflation, a stock's growth contribution can never increase more than <strong>4x the growth rate</strong> from the previous round. This is a per-round cap - stocks can continue growing beyond 4x in further rounds, but each round's growth is limited to prevent sudden price spikes.</li>
          <li><strong>Price Rounding:</strong> All stock prices are rounded up to the nearest whole number.</li>
          <li><strong>Minimum Sell Price:</strong> A stock's sell price is never less than $1.</li>
          <li><strong>Net Worth 💎:</strong> Your net worth = cash + stock sell value (for each stock you own). Stock sell value = current stock price - growth rate. Your net worth increases as more shares of a stock are bought (because the price goes up at the start of the next round), and changes based on actions and the corresponding index 📈 price</li>
          <li>Each stock pays <strong>dividends 🪙 on even-numbered rounds</strong> (Round 2, 4, 6, 8, etc.)</li>
          <li>Dividends are paid to all players who own shares of that stock - <strong>buy/sell strategically</strong> to maximize dividend income!</li>
          <li>You can own multiple shares of the same stock</li>
          <li><strong>Ownership Tokens:</strong> Below each stock card, you'll see colored tokens showing which players own shares. Tokens are displayed in two rows:
            <ul style={{ marginTop: '8px', paddingLeft: '20px' }}>
              <li><strong>Top row:</strong> Shows shares purchased in previous rounds (combined count, e.g., "x2" if purchased in round 1 and round 2)</li>
              <li><strong>Bottom row:</strong> Shows shares purchased in the current round (individual tokens, each showing "x1")</li>
              <li>A divider line separates the two rows for easy visual distinction</li>
              <li>Hover over any token to see the purchase round and purchase price</li>
            </ul>
          </li>
          <li><strong>Important:</strong> You <strong>cannot buy and sell the same stock in the same turn</strong></li>
        </ul>
        
        <div style={{ marginTop: '24px', padding: '20px', backgroundColor: 'rgba(255, 255, 255, 0.05)', borderRadius: '8px', border: '1px solid rgba(255, 255, 255, 0.1)' }}>
          <h4 style={{ marginTop: 0, marginBottom: '16px', fontSize: '1.1rem' }}>📊 Example Stock Cards</h4>
          <p style={{ fontSize: '0.95rem', marginBottom: '20px', color: '#aaa' }}>
            Here are examples of stock cards you'll see in the game. Each card shows:
          </p>
          <ul style={{ fontSize: '0.9rem', marginBottom: '20px', paddingLeft: '20px', color: '#bbb' }}>
            <li><strong>Header:</strong> Sector emoji (💻 Tech, 🏦 Finance, 🏭 Industrial, 🧬 Health & Science) and stock name</li>
            <li><strong>Description:</strong> Flavor text describing the company</li>
            <li><strong>🪙 Dividend:</strong> Cash paid (or cost) on even rounds - can be positive, zero, or negative</li>
            <li><strong>📈 Growth:</strong> Price increase per share owned at round start</li>
            <li><strong>💲 Price:</strong> Current market price with price change indicator (▲ green = up, ▼ red = down)</li>
            <li><strong>🎴 Action Cards:</strong> Manipulator cards applied to the stock (shown as small cards on top)</li>
          </ul>
          <div style={{ display: 'flex', gap: '20px', flexWrap: 'wrap', justifyContent: 'center', marginTop: '24px' }}>
            {exampleStocks.map((stock, idx) => {
              const stockOwnershipCounts = { [stock.name]: stock.priceBreakdown.stocksOwned || 0 };
              return (
                <div key={idx} style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', maxWidth: '200px' }}>
                  <StockCardView
                    stock={stock}
                    indexes={exampleIndexes}
                    styleCardSize={cardSize}
                    stockOwnershipCounts={stockOwnershipCounts}
                    stockOwnershipByPlayer={{}}
                    playerPortfolios={{}}
                    playerColors={{}}
                    playerEmojis={{}}
                  />
                  <div style={{ marginTop: '12px', fontSize: '0.85rem', textAlign: 'center', color: '#999' }}>
                    <p style={{ margin: '0 0 8px 0', fontWeight: 'bold', color: '#ccc' }}>{stock.displayValue || stock.name}</p>
                    <p style={{ margin: '4px 0', fontSize: '0.8rem' }}>
                      <strong>Sector:</strong> {stock.industrySector === 'tech' ? '💻 Tech' : stock.industrySector === 'finance' ? '🏦 Finance' : stock.industrySector}
                    </p>
                    <p style={{ margin: '4px 0', fontSize: '0.8rem' }}>
                      <strong>Base Cost:</strong> ${stock.baseCost}
                    </p>
                    <p style={{ margin: '4px 0', fontSize: '0.8rem' }}>
                      <strong>Growth:</strong> {stock.growth}x per share
                    </p>
                    <p style={{ margin: '4px 0', fontSize: '0.8rem' }}>
                      <strong>Dividend:</strong> {stock.dividend >= 0 ? '+' : ''}{stock.dividend} 🪙
                    </p>
                    <p style={{ margin: '4px 0', fontSize: '0.8rem' }}>
                      <strong>Current Price:</strong> ${stock.priceBreakdown.marketPrice}
                      {stock.priceBreakdown.priceChange !== 0 && (
                        <span style={{ color: stock.priceBreakdown.priceChange > 0 ? '#4caf50' : '#f44336', marginLeft: '4px' }}>
                          ({stock.priceBreakdown.priceChange > 0 ? '+' : ''}{stock.priceBreakdown.priceChange})
                        </span>
                      )}
                    </p>
                    {stock.priceBreakdown.stocksOwned > 0 && (
                      <p style={{ margin: '4px 0', fontSize: '0.8rem', color: '#4caf50' }}>
                        <strong>Shares Owned:</strong> {stock.priceBreakdown.stocksOwned} (affects price)
                      </p>
                    )}
                    {stock.appliedActionCards && stock.appliedActionCards.length > 0 && (
                      <p style={{ margin: '4px 0', fontSize: '0.8rem', color: '#ff9800' }}>
                        <strong>Manipulators:</strong> {stock.appliedActionCards.map(c => c.name).join(', ')}
                      </p>
                    )}
                    {stock.dividend < 0 && (
                      <p style={{ margin: '8px 0 0 0', fontSize: '0.75rem', color: '#f44336', fontStyle: 'italic' }}>
                        ⚠️ Negative dividend - costs money on even rounds!
                      </p>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </div>

      <div className="rule-section">
        <h3>⚙️ Optional Rules (Game Options in Lobby)</h3>
        <p>The <strong>host</strong> can toggle these options in the lobby before starting the game. Other players see them as read-only.</p>
        <ul className="rules-list">
          <li><strong>₿ Crypto Sector:</strong> Adds crypto stocks and the crypto index to the game (default: off)</li>
          <li><strong>📈 Index Buying:</strong> Allow players to purchase sector index cards (default: on). When off, indexes are not purchasable.</li>
          <li><strong>🎁 Dividend Action Card:</strong> On dividend rounds (except the final round), all players receive 1 free action card after dividends are paid (default: on). Players with a full hand (6 cards) do not receive the bonus.</li>
          <li><strong>🫧 Bubble Events:</strong> Include bubble event cards that grow over time and can pop with big index swings (default: off)</li>
          <li><strong>⏰ First Player End-of-Round Action:</strong> First player gets 2 actions during their turn, then 1 action at end of round after everyone else has acted. Same turn timer is used for the end-of-round action (default: on).</li>
        </ul>
      </div>

      <div className="rule-section">
        <h3>📈 Indexes</h3>
        <ul className="rules-list">
          <li>There are 4 market indexes (or 5 if Crypto is enabled in Game Options), each representing a sector: <strong>Tech 💻</strong>, <strong>Finance 🏦</strong>, <strong>Industrial 🏭</strong>, and <strong>Health & Science 🧬</strong></li>
          <li>When <strong>Index Buying</strong> is enabled (default), players can buy and sell index cards like stocks. When disabled, indexes are not purchasable.</li>
          <li>Index prices change based on <strong>events</strong> that affect the market</li>
          <li>When an index price changes, all stocks in that sector are affected</li>
          <li>Index prices can go negative, which reduces stock prices</li>
          <li><strong>Index Dividends:</strong> Indexes pay dividends on even-numbered rounds (Round 2, 4, 6, 8). Dividend rates vary by sector:
            <ul style={{ marginTop: '8px', paddingLeft: '20px' }}>
              <li><strong>Tech 💻 and Health & Science 🧬:</strong> 10% of current value (rounded down)
                <ul style={{ marginTop: '4px', paddingLeft: '20px' }}>
                  <li>Index price $10-19: $1 dividend</li>
                  <li>Index price $20-29: $2 dividend</li>
                  <li>Index price $30-39: $3 dividend</li>
                </ul>
              </li>
              <li><strong>Finance 🏦 and Industrial 🏭:</strong> 30% of current value (rounded to nearest multiple of 3)
                <ul style={{ marginTop: '4px', paddingLeft: '20px' }}>
                  <li>Index price $10-19: $3 dividend</li>
                  <li>Index price $20-29: $6 dividend</li>
                  <li>Index price $30-39: $9 dividend</li>
                </ul>
              </li>
            </ul>
            This creates variety in index strategies - growth-focused sectors (Tech, Health & Science) have lower dividends but higher event volatility, while stable sectors (Finance, Industrial) offer better dividend income!
          </li>
        </ul>
        
        <div style={{ marginTop: '24px', padding: '20px', backgroundColor: 'rgba(255, 255, 255, 0.05)', borderRadius: '8px', border: '1px solid rgba(255, 255, 255, 0.1)' }}>
          <h4 style={{ marginTop: 0, marginBottom: '16px', fontSize: '1.1rem' }}>📈 Example Index Cards</h4>
          <p style={{ fontSize: '0.95rem', marginBottom: '16px', color: '#aaa' }}>
            Indexes represent market sectors. When an index price changes, all stocks in that sector are affected. When Index Buying is enabled, indexes can be bought and sold like stocks.
          </p>
          <div style={{ display: 'flex', gap: '20px', flexWrap: 'wrap', justifyContent: 'center' }}>
            {exampleIndexes.map((index, idx) => (
              <div key={idx} style={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                <IndexCardView
                  ix={index}
                  styleCardSize={cardSize}
                  activeEvents={[]}
                  visualEffects={[]}
                  indexOwnershipByPlayer={{}}
                  playerColors={{}}
                  playerEmojis={{}}
                />
                <div style={{ marginTop: '12px', fontSize: '0.85rem', textAlign: 'center', color: '#999', maxWidth: '180px' }}>
                  <p style={{ margin: '0 0 8px 0', fontWeight: 'bold', color: '#ccc' }}>
                    {index.emoji} {index.name.charAt(0).toUpperCase() + index.name.slice(1)} Index
                  </p>
                  <p style={{ margin: '4px 0', fontSize: '0.8rem' }}>
                    <strong>Current Price:</strong> ${index.price}
                    {index.price < 0 && (
                      <span style={{ color: '#f44336', marginLeft: '4px' }}>
                        (negative - reduces stock prices)
                      </span>
                    )}
                  </p>
                  <p style={{ margin: '4px 0', fontSize: '0.8rem', color: '#bbb' }}>
                    Affects all {index.name} sector stocks
                  </p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>

      <div className="rule-section">
        <h3>📰 Events</h3>
        <p>Events are drawn at the start of each round (Events phase) and affect the market:</p>
        <ul className="rules-list">
          <li><strong>Immediate Events:</strong> Affect index prices when drawn (start-timing) or at the end of the round (end-timing)</li>
          <li><strong>Bubble Events:</strong> (Optional—host can enable in Game Options.) Special events that accumulate value each round and may "pop" with dramatic effects
            <ul style={{ marginTop: '8px', paddingLeft: '20px' }}>
              <li>Bubbles grow each round, increasing their potential impact</li>
              <li>At the start of each round, bubbles may pop based on probability or die rolls</li>
              <li><strong>Special Rule:</strong> Only <strong>one bubble can be active at a time</strong></li>
              <li>If a new bubble is drawn while another bubble is active, the existing bubble <strong>immediately pops</strong> (as if it rolled successfully)</li>
              <li>When a bubble pops, it applies its accumulated effects to the market</li>
            </ul>
          </li>
          <li>Events are resolved in the Events phase before new events are drawn</li>
          <li>Event effects on index prices are visible immediately, affecting stock prices for that round</li>
        </ul>
        
        <div style={{ marginTop: '24px', padding: '20px', backgroundColor: 'rgba(255, 255, 255, 0.05)', borderRadius: '8px', border: '1px solid rgba(255, 255, 255, 0.1)' }}>
          <h4 style={{ marginTop: 0, marginBottom: '16px', fontSize: '1.1rem' }}>📰 Example Event Cards</h4>
          <p style={{ fontSize: '0.95rem', marginBottom: '16px', color: '#aaa' }}>
            Events come in two types: <strong>Immediate Events</strong> (affect markets right away) and <strong>Bubble Events</strong> (ongoing effects that may pop):
          </p>
          <div style={{ display: 'flex', gap: '20px', flexWrap: 'wrap', justifyContent: 'center' }}>
            {exampleEvents.map((event, idx) => (
              <div key={idx} style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', maxWidth: '220px' }}>
                <EventCardView
                  event={event}
                  styleCardSize={cardSize}
                  isNew={false}
                />
                <div style={{ marginTop: '12px', fontSize: '0.85rem', textAlign: 'center', color: '#999' }}>
                  <p style={{ margin: '0 0 8px 0', fontWeight: 'bold', color: '#ccc' }}>{event.displayValue || event.name}</p>
                  <p style={{ margin: '4px 0', fontSize: '0.8rem' }}>
                    <strong>Type:</strong> {event.conditionalEffects ? '🫧 Bubble Event' : '⚡ Immediate Event'}
                  </p>
                  {event.conditionalEffects ? (
                    <>
                      <p style={{ margin: '4px 0', fontSize: '0.8rem' }}>
                        <strong>Initial Effect:</strong> {event.effects.map(e => `${e.indexName} ${e.priceChange >= 0 ? '+' : ''}${e.priceChange}`).join(', ')}
                      </p>
                      <p style={{ margin: '4px 0', fontSize: '0.8rem', color: '#ff9800' }}>
                        <strong>May Pop:</strong> {event.conditionalEffects.effects.map(e => `${e.indexName} ${e.priceChange}`).join(', ')}
                      </p>
                      <p style={{ margin: '4px 0', fontSize: '0.75rem', color: '#bbb', fontStyle: 'italic' }}>
                        Rolls 1-2 on d6 to pop (33% chance)
                      </p>
                    </>
                  ) : (
                    <p style={{ margin: '4px 0', fontSize: '0.8rem' }}>
                      <strong>Effect:</strong> {event.effects.map(e => `${e.indexName} ${e.priceChange >= 0 ? '+' : ''}${e.priceChange}`).join(', ')}
                    </p>
                  )}
                  {event.conditionalEffects && event.roundsActive > 1 && (
                    <p style={{ margin: '4px 0', fontSize: '0.75rem', color: '#4caf50' }}>
                      Active for multiple rounds
                    </p>
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>

      <div className="rule-section">
        <h3>💰 Dividends</h3>
        <p>Dividends are paid during the End of Round phase on even-numbered rounds (2, 4, 6, 8):</p>
        <ul className="rules-list">
          <li><strong>Stock Dividends:</strong> Each stock has a <strong>dividend value</strong> (can be positive, zero, or negative). On dividend rounds, you receive (or pay) the dividend amount for each stock you own</li>
          <li><strong>Index Dividends:</strong> Indexes also pay dividends! Dividend rates vary by sector:
            <ul style={{ marginTop: '8px', paddingLeft: '20px' }}>
              <li><strong>Tech 💻 and Health & Science 🧬:</strong> 10% of current value (rounded down)
                <ul style={{ marginTop: '4px', paddingLeft: '20px' }}>
                  <li>Example: Tech index worth $25 = $2 dividend per index owned</li>
                </ul>
              </li>
              <li><strong>Finance 🏦 and Industrial 🏭:</strong> 30% of current value (rounded to nearest multiple of 3)
                <ul style={{ marginTop: '4px', paddingLeft: '20px' }}>
                  <li>Example: Finance index worth $25 = $6 dividend per index owned</li>
                </ul>
              </li>
            </ul>
          </li>
          <li><strong>Dividend Bonus - Action Cards:</strong> If the host enables "Dividend Action Card" in Game Options, on dividend rounds (but not the final round) all players automatically receive <strong>1 free action card</strong>. This bonus is given after dividends are paid. Players with a full hand (6 cards) will not receive the bonus card.</li>
          <li><strong>Positive dividends:</strong> Stocks and indexes with good fundamentals reward long-term holding</li>
          <li><strong>Negative dividends:</strong> High-growth stocks may drain your cash - sell before dividend rounds!</li>
          <li><strong>Strategy:</strong> Time your purchases and sales around dividend rounds to maximize income. Index dividends make buying indexes a more viable long-term strategy! When enabled, the dividend action card bonus adds extra value to holding stocks through dividend rounds.</li>
        </ul>
      </div>

      <div className="rule-section">
        <h3>🎴 Action Cards</h3>
        <p>Players start with 2 action cards and can draw more during their turn (max hand size is 6 cards). <strong>Playing an action card costs 1 action.</strong></p>
        <ul className="rules-list">
          <li><strong>Insider Trading:</strong> Peek at the next event card to predict market movements. <strong>Does not consume an action</strong> - use it freely!</li>
          <li><strong>Delayed Manipulator Cards:</strong> These cards take effect at the <strong>end of the current round</strong> (not at the start of the next round), affecting prices before the next round begins:
            <ul style={{ marginTop: '4px', paddingLeft: '20px', listStyleType: 'disc' }}>
              <li><strong>FOMO (Influencer Hype, Merger Speculation, Analyst Hype):</strong> Add +5 to a stock's price at the end of the round</li>
              <li><strong>Sinking Ship (CEO Scandal, Accounting Scandal, Executive Misconduct):</strong> Reduce a stock's price by 5 at the end of the round</li>
              <li><strong>Rug Pull (when used on a stock):</strong> In addition to selling all your shares, the stock gets a delayed −5 at end of round (same as Sinking Ship)</li>
            </ul>
            Multiple FOMO, Sinking Ship, or Rug Pull delayed effects can be played on the same stock; their effects stack at the end of the round.
          </li>
          <li><strong>Fake News:</strong> All previous market perceptions erased. The card stays on the stock until end of round, then removes all manipulator cards (FOMO, Sinking Ship) <strong>before</strong> they take effect. Playing a second Fake News on the same stock cancels both out. <strong>Note:</strong> Does NOT remove Trading Halt.</li>
          <li><strong>Trading Halt:</strong> Halt all actions on a stock (no buying, selling, or action cards allowed) until your next turn. Only removable by playing another Trading Halt card on the same stock. The halt automatically expires when you reach your next turn.</li>
          <li><strong>Rug Pull:</strong> Sell all your holdings of a specific stock or index in a single action. When used on a <strong>stock</strong>, that stock also gets a delayed −5 price effect at end of round (like Sinking Ship), hurting remaining holders. Can be used on any stock or index you own. Note: Cannot sell stocks that are under Trading Halt.</li>
          <li><strong>Forced Sale:</strong> Force another player to sell one share at sell rate. Select an opponent's ownership token to trigger the sale - the affected player receives cash at sell rate. A conflict of interest card that disrupts competitors' portfolios.</li>
          <li><strong>Spin Cycle:</strong> Spin the narrative to another stock by moving all unprocessed delayed manipulator cards (FOMO/Sinking Ship) from one stock to another. First select the source stock (must have at least one unprocessed delayed manipulator), then select the target stock (must have at least one share owned by another player). <strong>Cannot be used on any stock under Trading Halt</strong> (neither source nor target). Lets you send negative narrative to opponents' stocks or move positive narrative away.</li>
          <li><strong>Double Down:</strong> Buy one share of a stock you already own at a discount. Discount = (your current shares of that stock) × (that stock's growth rate). One action covers both playing the card and the buy; the buy counts toward your normal limit of 2 stock purchases per turn. Minimum price $1. Incentivizes pumping: the more you hold, the cheaper the next share.</li>
        </ul>
        <p style={{ marginTop: '12px' }}><strong>Special Rules for Action Cards:</strong></p>
        <ul className="rules-list" style={{ marginTop: '8px' }}>
          <li>Delayed manipulator cards (FOMO, Sinking Ship) take effect at the <strong>end of the current round</strong> and remain visible on the stock after taking effect</li>
          <li><strong>Stacking:</strong> Multiple FOMO or Sinking Ship cards can be played on the same stock; their effects are summed when they resolve at the end of the round.</li>
          <li><strong>Cancelling Pairs:</strong> If both a FOMO (+5) and Sinking Ship (-5) are on the same stock at end of round, they cancel each other out and are both removed without affecting the price.</li>
          <li><strong>Timing:</strong> Delayed manipulators affect prices at the end of the round, ensuring they impact final prices and scoring even in the final round</li>
          <li>Manipulator cards can be removed by Fake News (takes effect at end of round, before manipulators resolve)</li>
          <li>Trading Halt can only be removed by playing another Trading Halt card on the same stock, or it automatically expires when the player who applied it reaches their next turn</li>
          <li>Action cards that target stocks require selecting the stock first</li>
          <li>Playing an action card costs one action, except for Insider Trading which is free</li>
          <li><strong>Discard & Redraw:</strong> Once per turn, you can spend 1 action to discard 1-3 cards from your hand and draw the same number of replacements. Use this to cycle out cards you can't use effectively.</li>
        </ul>
        
        <div style={{ marginTop: '24px', padding: '20px', backgroundColor: 'rgba(255, 255, 255, 0.05)', borderRadius: '8px', border: '1px solid rgba(255, 255, 255, 0.1)' }}>
          <h4 style={{ marginTop: 0, marginBottom: '16px', fontSize: '1.1rem' }}>🎴 Example: Stocks with Applied Action Cards</h4>
          <p style={{ fontSize: '0.95rem', marginBottom: '16px', color: '#aaa' }}>
            When action cards are played on stocks, they appear as small cards stacked on top. Here are examples:
          </p>
          <div style={{ display: 'flex', gap: '20px', flexWrap: 'wrap', justifyContent: 'center' }}>
            {[
              {
                name: "Cloud Nine Data",
                baseCost: 16,
                growth: 2,
                dividend: 4,
                industrySector: "tech",
                description: "Infinite server farms storing cat videos and your entire digital life.",
                priceBreakdown: {
                  marketPrice: 23,
                  baseCost: 16,
                  indexPrice: 2,
                  growthCost: 0,
                  stocksOwned: 0,
                  manipulatorEffect: 5,
                  previousPrice: 18,
                  priceChange: 5
                },
                appliedActionCards: [
                  {
                    name: "FOMO",
                    actionType: "delayed_manipulate",
                    effectValue: 5,
                    processed: true,
                    emoji: "🚀"
                  }
                ],
                cardNote: "FOMO card adds +5 at end of round (already processed)"
              }
            ].filter(stock => stock.appliedActionCards && stock.appliedActionCards[0].actionType === 'delayed_manipulate').map((stock, idx) => {
              const stockOwnershipCounts = { [stock.name]: stock.priceBreakdown.stocksOwned || 0 };
              return (
                <div key={idx} style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', maxWidth: '200px' }}>
                  <StockCardView
                    stock={stock}
                    indexes={exampleIndexes}
                    styleCardSize={cardSize}
                    stockOwnershipCounts={stockOwnershipCounts}
                    stockOwnershipByPlayer={{}}
                    playerPortfolios={{}}
                    playerColors={{}}
                    playerEmojis={{}}
                  />
                  <div style={{ marginTop: '12px', fontSize: '0.85rem', textAlign: 'center', color: '#999' }}>
                    <p style={{ margin: '0 0 8px 0', fontWeight: 'bold', color: '#ccc' }}>{stock.displayValue || stock.name}</p>
                    <p style={{ margin: '4px 0', fontSize: '0.8rem', color: '#ff9800' }}>
                      <strong>Applied Card:</strong> {stock.appliedActionCards[0].displayValue || stock.appliedActionCards[0].name}
                    </p>
                    <p style={{ margin: '4px 0', fontSize: '0.8rem' }}>
                      <strong>Effect:</strong> {stock.appliedActionCards[0].effectValue >= 0 ? '+' : ''}{stock.appliedActionCards[0].effectValue} to price
                    </p>
                    <p style={{ margin: '4px 0', fontSize: '0.8rem' }}>
                      <strong>Price:</strong> ${stock.priceBreakdown.marketPrice}
                      <span style={{ color: stock.priceBreakdown.priceChange > 0 ? '#4caf50' : '#f44336', marginLeft: '4px' }}>
                        ({stock.priceBreakdown.priceChange > 0 ? '+' : ''}{stock.priceBreakdown.priceChange})
                      </span>
                    </p>
                    <p style={{ margin: '8px 0 0 0', fontSize: '0.75rem', color: '#bbb', fontStyle: 'italic' }}>
                      {stock.cardNote}
                    </p>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </div>

      <div className="rule-section">
        <h3>🔌 Disconnections & Reconnections</h3>
        <ul className="rules-list">
          <li>If a player disconnects, they remain in the game as a disconnected player (shown with a 🔌 indicator)</li>
          <li>If a player disconnects <strong>before</strong> their turn starts, their turn is skipped. If they disconnect <strong>during</strong> their turn, the turn timer continues and the turn ends when time expires.</li>
          <li>The host can <strong>kick</strong> a disconnected player (✕ button) to replace them with a bot</li>
          <li>Players can reconnect at any time and resume playing on their next turn</li>
          <li>The game continues as long as at least one player remains connected</li>
          <li>Your session is saved for 24 hours - if you disconnect and return, you'll automatically rejoin your game</li>
        </ul>
      </div>

      <div className="rule-section">
        <h3>⭐ Special Rules</h3>
        <ul className="rules-list">
          <li><strong>Bubble Rule:</strong> Only one bubble event can be active at a time. Drawing a new bubble immediately pops any existing bubble.</li>
          <li><strong>Dividends:</strong> Paid on even-numbered rounds (2, 4, 6, 8, etc.) to all stock owners</li>
          <li><strong>Delayed Manipulators:</strong> FOMO and Sinking Ship cards take effect at the <strong>end of the current round</strong> and remain visible on the stock</li>
          <li><strong>Index Dividends:</strong> Indexes pay dividends on even-numbered rounds (10% for Tech/Health & Science, 30% for Finance/Industrial)</li>
          <li><strong>Turn Order:</strong> Shuffled on Round 1, then rotated each subsequent round (first player moves to end)</li>
          <li><strong>Game Length:</strong> Maximum 10 rounds. The number of rounds is calculated as the largest multiple of the number of players that doesn't exceed 10, ensuring each player gets at least one turn in each starting position. 6-round games give players 4 actions per turn to compensate for the shorter game length.</li>
        </ul>
      </div>

      <div className="rule-section">
        <h3>🏆 Winning</h3>
        <p>After all rounds are complete, the player with the <strong>highest net worth</strong> wins!</p>
        <p style={{ marginTop: '8px' }}>Net worth = Cash + Stock Sell Value (for each stock you own)</p>
        <p style={{ marginTop: '4px', fontSize: '0.95rem', color: '#888' }}>Stock Sell Value = (Current Stock Price - Growth Rate) × Shares Owned</p>
      </div>

      <div className="rule-section" style={{ 
        marginTop: '24px', 
        paddingTop: '20px', 
        borderTop: '1px solid rgba(255, 255, 255, 0.1)',
        textAlign: 'center'
      }}>
        <p style={{ fontSize: '0.9rem', color: '#888', margin: '0 0 8px 0' }}>
          Game created by{' '}
          <a 
            href="https://www.linkedin.com/in/paul-morris-perthwa/" 
            target="_blank" 
            rel="noopener noreferrer"
            style={{ 
              color: '#4a90e2', 
              textDecoration: 'none',
              fontWeight: '500'
            }}
            onMouseEnter={(e) => e.target.style.textDecoration = 'underline'}
            onMouseLeave={(e) => e.target.style.textDecoration = 'none'}
          >
            Paul Morris
          </a>
        </p>
        <p style={{ fontSize: '0.85rem', color: '#999', margin: 0, lineHeight: '1.5' }}>
          Built with assistance from{' '}
          <a 
            href="https://cursor.sh" 
            target="_blank" 
            rel="noopener noreferrer"
            style={{ 
              color: '#4a90e2', 
              textDecoration: 'none',
              fontWeight: '500'
            }}
            onMouseEnter={(e) => e.target.style.textDecoration = 'underline'}
            onMouseLeave={(e) => e.target.style.textDecoration = 'none'}
          >
            Cursor
          </a>
          {' '}and{' '}
          <a 
            href="https://www.anthropic.com/claude" 
            target="_blank" 
            rel="noopener noreferrer"
            style={{ 
              color: '#4a90e2', 
              textDecoration: 'none',
              fontWeight: '500'
            }}
            onMouseEnter={(e) => e.target.style.textDecoration = 'underline'}
            onMouseLeave={(e) => e.target.style.textDecoration = 'none'}
          >
            Claude
          </a>
          {' '}(Anthropic)
        </p>
      </div>
    </div>
  );

  if (variant === "popup") {
    return (
      <div style={{
        width: '100%',
        maxHeight: '70vh',
        overflowY: 'auto',
        padding: '20px 24px',
        boxSizing: 'border-box'
      }}>
        {showTitle && <h2 style={{ marginTop: 0, marginBottom: '20px' }}>📖 Game Manual</h2>}
        {rulesContent}
      </div>
    );
  }

  if (variant === "collapsible") {
    return (
      <div className="lobby-card">
        <div 
          className="card-header"
          style={{ cursor: 'pointer' }}
          onClick={onToggle}
        >
          <h2>📖 Game Manual</h2>
          <span style={{ 
            fontSize: '18px', 
            transition: 'transform 0.3s', 
            transform: isExpanded ? 'rotate(180deg)' : 'rotate(0deg)' 
          }}>
            ▼
          </span>
        </div>
        
        {isExpanded && (
          <div className="card-content">
            {rulesContent}
          </div>
        )}
      </div>
    );
  }

  // Default: card variant (for login page)
  return (
    <div className="rules-card">
      <div className="rules-header">
        <h2>🎮 How to Play</h2>
      </div>
      {rulesContent}
    </div>
  );
};

export default GameRules;

